import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { DollarSign, Loader2, ChevronLeft, Building2, TrendingUp, Cpu } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { api } from '@/lib/api';

const usd = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PLAN_STYLES = {
  active: 'text-emerald-500',
  canceling: 'text-amber-500',
  past_due: 'text-red-500',
  canceled: 'text-muted-foreground',
};

function Stat({ icon: Icon, label, value, sub, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-2xl font-semibold text-foreground">{value}</div>
      {sub ? <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div> : null}
    </Card>
  );
}

export default function PlatformBillingRollupPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setData(await api.getPlatformBillingRollup());
      } catch (e) {
        setError(e.message || 'Could not load billing rollup');
        toast.error(e.message || 'Could not load billing rollup');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const t = data?.totals || {};

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6" data-testid="platform-billing-page">
      <button onClick={() => navigate('/platform-console')} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3" data-testid="platform-billing-back">
        <ChevronLeft className="w-4 h-4" /> Platform Console
      </button>
      <div className="flex items-center gap-2 mb-1">
        <DollarSign className="w-5 h-5 text-primary" />
        <h1 className="text-xl font-semibold text-foreground">Billing Overview</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-5">
        Deployment-wide subscription and AI-usage totals across every workspace, plus a per-tenant breakdown.
        {data?.month ? ` AI usage shown for ${data.month}.` : ''}
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-10 justify-center" data-testid="platform-billing-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading billing rollup…
        </div>
      ) : error ? (
        <Card className="p-8 text-center text-sm text-red-500" data-testid="platform-billing-error">{error}</Card>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid="platform-billing-totals">
            <Stat icon={TrendingUp} label="MRR" value={usd(t.mrr_usd)} sub={`ARR ${usd(t.arr_usd)}`} testid="stat-mrr" />
            <Stat icon={Building2} label="Active subscriptions" value={t.active_subscriptions ?? 0} sub={`${t.tenants ?? 0} workspaces total`} testid="stat-subs" />
            <Stat icon={Cpu} label="AI billed (MTD)" value={usd(t.ai_billed_usd)} sub={`Cost ${usd(t.ai_cost_usd)}`} testid="stat-ai-billed" />
            <Stat icon={DollarSign} label="AI margin (MTD)" value={usd(t.ai_margin_usd)} sub={`${(t.ai_calls ?? 0).toLocaleString()} calls`} testid="stat-ai-margin" />
          </div>

          <Card className="overflow-hidden" data-testid="platform-billing-table">
            <div className="px-4 py-3 border-b border-border/40 text-sm font-medium">Per-tenant breakdown</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border/40">
                    <th className="text-left font-medium px-4 py-2">Workspace</th>
                    <th className="text-left font-medium px-4 py-2">Plan</th>
                    <th className="text-right font-medium px-4 py-2">MRR</th>
                    <th className="text-right font-medium px-4 py-2">AI billed</th>
                    <th className="text-right font-medium px-4 py-2">AI cost</th>
                    <th className="text-right font-medium px-4 py-2">Calls</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.tenants || []).map((r) => (
                    <tr key={r.tenant_id} className="border-b border-border/20 hover:bg-muted/20" data-testid={`tenant-row-${r.tenant_id}`}>
                      <td className="px-4 py-2 text-foreground">{r.tenant_name}</td>
                      <td className="px-4 py-2">
                        <span className="capitalize">{r.plan}</span>
                        <span className={`ml-2 text-[11px] capitalize ${PLAN_STYLES[r.plan_status] || 'text-muted-foreground'}`}>{r.plan_status}</span>
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">{usd(r.mrr_usd)}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{usd(r.ai_billed_usd)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{usd(r.ai_cost_usd)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{(r.ai_calls || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                  {(data?.tenants || []).length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No tenants found.</td></tr>
                  )}
                </tbody>
                <tfoot>
                  <tr className="border-t border-border/40 font-medium bg-muted/20" data-testid="platform-billing-totals-row">
                    <td className="px-4 py-2">Deployment total</td>
                    <td className="px-4 py-2 text-muted-foreground">{t.active_subscriptions ?? 0} paid</td>
                    <td className="px-4 py-2 text-right tabular-nums">{usd(t.mrr_usd)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{usd(t.ai_billed_usd)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{usd(t.ai_cost_usd)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{(t.ai_calls ?? 0).toLocaleString()}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
